import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Plural, Trans } from '@lingui/react/macro'

import type { PhotosApi } from '@jotta/types/PhotosApi'
import { showToast } from '@jotta/ui/ToastProvider'

import { getAlbumTitle } from '../Album/AlbumThumb/AlbumThumb'
import { useDispatchPhotoActionMutation } from '../PhotoActions/useDispatchPhotoActionMutation'

import styles from './SharedPhotosPage.module.scss'

export interface SharedPhotosPageRowProps {
  album: PhotosApi.AlbumInfo
}
export const SharedPhotosPageRow = observer<SharedPhotosPageRowProps>(
  function SharedPhotosPageRow({ album }) {
    const mutation = useDispatchPhotoActionMutation('UNSHARE_ALBUM')

    return (
      <Fragment key={album.id}>
        <Link
          className={clsx(styles.thumb, styles.thumbColumn)}
          to={`/share/${album.shareInfo?.uri}`}
          aria-label={t`Copy shared photos or shared album link`}
        >
          {album.thumbUrl && <img src={album.thumbUrl} alt={album.title} />}
        </Link>
        <div className={clsx(styles.column, styles.titleColumn)}>
          <h3>{getAlbumTitle(album)}</h3>
          <Link
            to={`/share/${album.shareInfo?.uri}`}
            aria-label={t`Copy shared photos or shared album link`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Trans>View photos</Trans>
          </Link>
        </div>
        <div className={clsx(styles.column, styles.contentColumn)}>
          {album.isSharedPhotos ? (
            <Plural value={album.total} one="# photo" other="# photos" />
          ) : album.total ? (
            <Plural
              value={album.total}
              one="Album with # photo"
              other="Album with # photos"
            />
          ) : (
            <Trans>Empty album</Trans>
          )}
        </div>
        <div className={clsx(styles.column, styles.createdColumn)}>
          {album && dayjs(album.createdDate, { utc: false }).fromNow()}
        </div>
        <div className={clsx(styles.column, styles.copyLinkColumn)}>
          <div className={styles.copylink}>
            <div className={styles.link}>
              {window.location.origin}/share/
              {album.shareInfo?.uri}
            </div>
            <button
              aria-label={t`Copy shared photos or shared album link`}
              className={styles.copyLinkButton}
              onClick={() => {
                const shareUri = album.shareInfo?.uri
                if (shareUri) {
                  navigator.clipboard
                    .writeText(`${window.location.origin}/share/${shareUri}`)
                    .then(() =>
                      showToast({
                        message: t`Copied to clipboard`,
                        duration: 5000,
                        hasCloseButton: true,
                      }),
                    )
                    .catch(reason => {
                      showToast({
                        message: t`Copy to clipboard failed`,
                        hasCloseButton: true,
                      })
                      reportError(reason)
                    })
                }
              }}
            >
              <Trans>Copy</Trans>
            </button>
          </div>

          <button
            className={styles.removelink}
            disabled={mutation.isPending}
            aria-label={t`Remove shared photos or shared album link`}
            onClick={() => mutation.mutate([album.id])}
          >
            <Trans>Remove link</Trans>
          </button>
        </div>
        <hr />
      </Fragment>
    )
  },
)
